import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, InputAdornment, ToggleButton, ToggleButtonGroup, Avatar,
} from '@mui/material';
import { Storefront, Inbox, Search, Inventory2, WarningAmber, DoDisturb, Image as ImageIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import StudioSelector, { StudioOption } from './StudioSelector';

/**
 * Below this, a variant is flagged "Low". Matches the amber chip threshold the
 * Items grid already uses, so a product reads the same on both screens.
 */
const LOW_STOCK_AT = 5;

interface Props {
  selectedStudio: StudioOption | null;
  onStudioChange: (s: StudioOption | null) => void;
  onToast: (msg: string, severity: 'success' | 'error') => void;
}

/** One table row = one sellable unit: a variant, or a whole product if it has none. */
interface StockRow {
  productId: string;
  productTitle: string;
  category: string;
  variantLabel: string;
  sku: string;
  stock: number;
  image: string | null;
  productActive: boolean;
  variantActive: boolean;
}

/**
 * The image for a specific variant — the colour actually chosen, not the
 * product default. Mirrors the backend's resolveVariantImage: a variant's photo
 * lives on its option value (Colour → Red → [url]), so find the visual option,
 * read this variant's value, and take that value's first image. Falls back to
 * the product image for size-only variants or colours with no photo.
 */
function variantImage(item: any, variant: any | null): string | null {
  const productImage = Array.isArray(item?.images) && item.images.length ? item.images[0] : null;
  if (!variant) return productImage;
  const options: any[] = Array.isArray(item?.options) ? item.options : [];
  for (const opt of options) {
    if (!opt?.valueImages) continue; // only a visual dimension has photos
    const value = variant.optionValues?.[opt.name];
    if (value == null) continue;
    const imgs = opt.valueImages?.[value];
    if (Array.isArray(imgs) && imgs.length) return imgs[0];
  }
  return productImage;
}

type StatusKind = 'out' | 'low' | 'ok';
const statusOf = (r: StockRow): StatusKind =>
  r.stock <= 0 ? 'out' : r.stock <= LOW_STOCK_AT ? 'low' : 'ok';

/**
 * Stock at a glance — every variant of every product on one filterable table.
 *
 * Built from the same `getMerchandise` call the Items grid uses (stock lives on
 * each variant), so it needs no new endpoint and can never disagree with the
 * adjust-stock dialog. The point is speed: filter by product, category or a
 * size/colour and read the number, without opening a product to find it.
 */
export default function MerchandiseStockTab({ selectedStudio, onStudioChange, onToast }: Props) {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  const studioId = selectedStudio?._id;

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState<'all' | 'attention'>('all');

  const fetchItems = useCallback(async () => {
    if (!isStudio && !studioId) { setItems([]); return; }
    setLoading(true);
    try {
      const res: any = await apiService.getMerchandise({
        ...(studioId ? { studioId } : {}),
        pageSize: 100,
      });
      setItems(res?.merchandise || []);
    } catch (err: any) {
      onToast(err?.response?.data?.message || 'Failed to load stock', 'error');
    } finally {
      setLoading(false);
    }
  }, [isStudio, studioId, onToast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Flatten products -> one row per active variant (or the product itself when
  // it has no variants). Inactive variants are dropped: they cannot be sold, so
  // their stock is not stock a member could ever receive.
  const rows: StockRow[] = useMemo(() => {
    const out: StockRow[] = [];
    for (const it of items) {
      const base = {
        productId: it._id,
        productTitle: it.title ?? '',
        category: it.category || '—',
        productActive: it.isActive !== false,
      };
      const variants: any[] = it.variants ?? [];
      if (variants.length) {
        for (const v of variants) {
          if (v.isActive === false) continue;
          out.push({
            ...base,
            variantLabel: v.label || '—',
            sku: v.sku || '—',
            stock: v.stockQuantity ?? 0,
            image: variantImage(it, v),
            variantActive: true,
          });
        }
      } else {
        out.push({
          ...base,
          variantLabel: '—',
          sku: it.sku || '—',
          stock: it.stockQuantity ?? 0,
          image: variantImage(it, null),
          variantActive: true,
        });
      }
    }
    return out;
  }, [items]);

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter((c) => c && c !== '—'))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (category === 'all' ? true : r.category === category))
      .filter((r) => (status === 'attention' ? statusOf(r) !== 'ok' : true))
      .filter((r) =>
        q === ''
          ? true
          : r.productTitle.toLowerCase().includes(q) ||
            r.sku.toLowerCase().includes(q) ||
            r.variantLabel.toLowerCase().includes(q),
      )
      // Lowest stock first — whatever needs attention sits at the top.
      .sort((a, b) => a.stock - b.stock || a.productTitle.localeCompare(b.productTitle));
  }, [rows, search, category, status]);

  const totals = useMemo(() => {
    let out = 0, low = 0, units = 0;
    for (const r of rows) {
      units += r.stock;
      const s = statusOf(r);
      if (s === 'out') out++;
      else if (s === 'low') low++;
    }
    return { skus: rows.length, out, low, units };
  }, [rows]);

  const chipFor = (r: StockRow) => {
    const s = statusOf(r);
    if (s === 'out') return <Chip size="small" label="Out of stock" color="error" variant="outlined" />;
    if (s === 'low') return <Chip size="small" label={`Low · ${r.stock}`} color="warning" variant="outlined" />;
    return <Chip size="small" label="In stock" color="success" variant="outlined" />;
  };

  return (
    <Box>
      {/* Filter bar */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <StudioSelector value={selectedStudio} onChange={onStudioChange} required />
        <TextField
          size="small"
          placeholder="Search product, SKU or variant"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <TextField
          select size="small" label="Category" value={category}
          onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All categories</MenuItem>
          {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <ToggleButtonGroup
          size="small" exclusive value={status}
          onChange={(_, v) => v && setStatus(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="attention">Low / out</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !isStudio && !studioId ? (
        <Box textAlign="center" py={10}>
          <Storefront sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">Select a studio to view its stock</Typography>
        </Box>
      ) : (
        <>
          {/* KPI tiles */}
          <Grid container spacing={2} mb={3}>
            {[
              { icon: <Inventory2 color="action" />, label: 'Variants', value: totals.skus },
              { icon: <Inventory2 color="primary" />, label: 'Units in stock', value: totals.units.toLocaleString('en-IN') },
              { icon: <WarningAmber color="warning" />, label: `Low (≤ ${LOW_STOCK_AT})`, value: totals.low },
              { icon: <DoDisturb color="error" />, label: 'Out of stock', value: totals.out },
            ].map((k) => (
              <Grid size={{ xs: 6, md: 3 }} key={k.label}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      {k.icon}
                      <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700}>{k.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filtered.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Inbox sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                {rows.length === 0 ? 'No products yet' : 'Nothing matches your filters'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {['Product', 'Category', 'Variant', 'SKU', 'In stock', 'Status'].map((h, i) => (
                      <TableCell key={h} align={i === 4 ? 'right' : 'left'}>
                        <Typography variant="caption" fontWeight={700}>{h}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r, i) => (
                    <TableRow key={`${r.sku}-${i}`} hover sx={{ opacity: r.productActive ? 1 : 0.5 }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            variant="rounded"
                            src={r.image || undefined}
                            sx={{ width: 36, height: 36, bgcolor: 'grey.100' }}
                          >
                            <ImageIcon fontSize="small" sx={{ color: 'grey.400' }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{r.productTitle}</Typography>
                            {!r.productActive && (
                              <Typography variant="caption" color="text.disabled">hidden</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{r.category}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{r.variantLabel}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">{r.sku}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>{r.stock}</Typography>
                      </TableCell>
                      <TableCell>{chipFor(r)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
